import { Box, type BoxProps } from "@chakra-ui/react";
import {
  SparkRegisterInfo,
  SparkWithRegistrationSummary,
  calculateSparkRegisterFormFilledFields,
} from "@greenflagdate/shared";
import { useState } from "react";
import { useStoreData } from "../../lib/store/store";
import { LoginDialog } from "../LoginDialog/LoginDialog";
import { ErrorAlerts } from "./parts/ErrorAlerts";
import { Header } from "./parts/Header";
import { useSparkRegistration } from "./useSparkRegistration";
import { CheckoutView } from "./views/CheckoutView";
import { DraftNotice } from "./views/DraftNotice";
import { EmailStep } from "./views/EmailStep";
import { OtpStep } from "./views/OtpStep";
import { ProfileInfoStep } from "./views/ProfileInfoStep";
import { WaitlistOfferView } from "./views/WaitlistOfferView";

interface SparkRegisterFormProps extends BoxProps {
  spark: SparkWithRegistrationSummary;
  onRegister: () => void;
  onJoinWaitlist: () => void;
}

export const SparkRegisterForm = ({
  spark,
  onRegister,
  onJoinWaitlist,
  ...props
}: SparkRegisterFormProps) => {
  const storeData = useStoreData();
  const [loginDialogShown, setLoginDialogShown] = useState(false);

  const {
    view,
    loggedIn,
    sparkRegisterInfo,
    setSparkRegisterInfo,
    textOptIn,
    setTextOptIn,
    coupon,
    setCoupon,
    registerLoading,
    activeDiscount,
    isFree,
    isEffectivelyFree,
    errors,
    actions,
  } = useSparkRegistration({ spark, onRegister, onJoinWaitlist });

  if (view.kind === "unpublished") return <DraftNotice {...props} />;

  const updateInfo = (val: Partial<SparkRegisterInfo>) =>
    setSparkRegisterInfo((prev) => ({ ...prev, ...val }));

  return (
    <Box
      display="flex"
      flexDirection="column"
      // Fill the DrawerBody so steps that opt into a pinned footer
      // (ProfileInfoStep) can reach the bottom. Steps without a flex
      // child just sit at the top — empty space below is fine.
      flex="1"
      minH="0"
      gap="16px"
      pt="20px"
      fontFamily="body"
      color="fg.default"
      {...props}
    >
      {/* The header's "Already have an account? / Hi, X!" line is noise on
          the email + OTP steps — once auth is in progress the Sign in link
          is redundant. Show it only on the steps where it's actionable.
          On the profile step the header doubles as the step title so the
          log-out affordance shares a row with the heading. */}
      {view.step !== "email" && view.step !== "otp" && (
        <Header
          loggedIn={loggedIn}
          onSignInClick={() => setLoginDialogShown(true)}
          title={view.step === "profile" ? "A few quick details" : undefined}
        />
      )}
      <ErrorAlerts {...errors} />

      {view.step === "email" && (
        <EmailStep
          defaultEmail={sparkRegisterInfo.email}
          onSubmit={async (email) => {
            await actions.initiateLogin(email);
          }}
        />
      )}

      {view.step === "otp" && (
        <OtpStep
          email={sparkRegisterInfo.email || storeData.user?.email}
          confirmLabel={view.goal === "waitlist" ? "Join Wait List" : "Register"}
          onComplete={actions.onOtpComplete}
        />
      )}

      {view.step === "profile" && (
        <ProfileInfoStepWrapper
          sparkRegisterInfo={sparkRegisterInfo}
          updateInfo={updateInfo}
          textOptIn={textOptIn}
          onChangeTextOptIn={setTextOptIn}
          onSubmit={actions.onProfileSubmit}
        />
      )}

      {view.step === "waitlist-offer" && (
        <WaitlistOfferView
          gender={(
            sparkRegisterInfo.gender ||
            storeData.user?.gender ||
            ""
          ).toLowerCase()}
          onJoinWaitlist={actions.acceptWaitlistOffer}
        />
      )}

      {view.step === "checkout" && (
        <CheckoutView
          spark={spark}
          coupon={coupon}
          onCouponApplied={setCoupon}
          activeDiscount={activeDiscount}
          isFree={isFree}
          isEffectivelyFree={isEffectivelyFree}
          registerLoading={registerLoading}
          onRegister={actions.onCheckoutSubmit}
        />
      )}

      <LoginDialog
        isShown={loginDialogShown}
        onClose={() => setLoginDialogShown(false)}
      />
    </Box>
  );
};

// Profile step wrapper. Computes which fields to omit based on the user's
// existing profile. Policies live on the next/terminal step (CheckoutView)
// regardless of free/paid, so the profile step never collects them.
interface ProfileInfoStepWrapperProps {
  sparkRegisterInfo: SparkRegisterInfo;
  updateInfo: (val: Partial<SparkRegisterInfo>) => void;
  textOptIn: boolean;
  onChangeTextOptIn: (checked: boolean) => void;
  onSubmit: () => void;
}

const ProfileInfoStepWrapper = ({
  sparkRegisterInfo,
  updateInfo,
  textOptIn,
  onChangeTextOptIn,
  onSubmit,
}: ProfileInfoStepWrapperProps) => {
  const storeData = useStoreData();

  return (
    <ProfileInfoStep
      omitFields={[
        "email",
        ...calculateSparkRegisterFormFilledFields(storeData.user || {}),
      ]}
      sparkRegisterInfo={sparkRegisterInfo}
      onChangeRegisterInfo={updateInfo}
      textOptIn={textOptIn}
      onChangeTextOptIn={onChangeTextOptIn}
      onSubmitSuccess={onSubmit}
      buttonLabel="Continue"
    />
  );
};
